import type { OptionsDocument, QuoteArea } from 'paintscout'
import type { UpdateableAreaSubstrate } from '../../../../index'
import { getObjectLabels } from '../../../../../util'
import { parseNumber } from '../../../../../../calculator'

// Get dimensions for the UpdateableAreaSubstrate to be displayed to crew
export default function getDimensions({
  rateType,
  areaType,
  item,
  area,
  options
}: {
  rateType: string
  areaType: string
  item: UpdateableAreaSubstrate
  options: OptionsDocument
  area: QuoteArea
}) {
  let label = ''
  let value = 0
  const { unit } = getObjectLabels({ options })
  if (rateType === 'quantity') {
    value = Number(item.quantity)
  } else {
    if (areaType === 'surface') {
      const length = item.length?.useCustom ? item.length.custom : item.length?.default
      const height = item.height?.useCustom ? item.height.custom : item.height?.default
      const customSubstrateDimensions = item.length?.useCustom || item.height?.useCustom

      const areaLnft = area.dimensions.override_lnft ? area.dimensions.lnft : area.dimensions.calculated_lnft
      const areaSqft = area.dimensions.override_sqft ? area.dimensions.sqft : area.dimensions.calculated_sqft

      const lnft = customSubstrateDimensions ? length : areaLnft
      const sqft = customSubstrateDimensions ? length * height : areaSqft

      if (rateType === 'lnft') {
        label = unit.abbreviation.linear
        const dimension = lnft
        value = typeof dimension === 'number' ? dimension : Number.parseFloat(dimension)
      } else {
        label = unit.abbreviation.square
        const dimension = sqft
        value = typeof dimension === 'number' ? dimension : Number.parseFloat(dimension)
      }
    } else {
      // A room substrate can carry its own length/width/height. When any of them is custom, derive the
      // dimension from the substrate (the same formulas the calculator and the area-grouped WO use)
      // instead of the whole area's dimension (#24052). Like those, this assumes a rectangular room:
      // perimeter is 2 * (length + width), walls are perimeter * height, ceiling/floor are length * width.
      const length = parseNumber(item.length?.useCustom ? item.length.custom : item.length?.default)
      const width = parseNumber(item.width?.useCustom ? item.width.custom : item.width?.default)
      const height = parseNumber(item.height?.useCustom ? item.height.custom : item.height?.default)
      const customSubstrateDimensions = item.length?.useCustom || item.width?.useCustom || item.height?.useCustom

      if (rateType === 'lnft') {
        const dimension = area.dimensions.override_lnft ? area.dimensions.lnft : area.dimensions.calculated_lnft
        value = customSubstrateDimensions ? decimalPrecision(2 * (length + width)) : toNumber(dimension)
        label = unit.abbreviation.linear
      } else if (['sqft_walls', 'sqftWalls'].includes(rateType)) {
        const dimension = area.dimensions.override_sqft_walls
          ? area.dimensions.sqft_walls
          : area.dimensions.calculated_sqft_walls
        value = customSubstrateDimensions ? decimalPrecision(2 * (length + width) * height) : toNumber(dimension)

        label = unit.abbreviation.square
      } else if (['sqft_ceiling', 'sqftCeiling'].includes(rateType)) {
        label = unit.abbreviation.square

        const dimension = area.dimensions.override_sqft_ceiling
          ? area.dimensions.sqft_ceiling
          : area.dimensions.calculated_sqft_ceiling
        value = customSubstrateDimensions ? decimalPrecision(length * width) : toNumber(dimension)
      } else if (['sqft_floor', 'sqftFloor'].includes(rateType)) {
        const dimension = area.dimensions.override_sqft_floor
          ? area.dimensions.sqft_floor
          : area.dimensions.calculated_sqft_floor
        value = customSubstrateDimensions ? decimalPrecision(length * width) : toNumber(dimension)
        label = unit.abbreviation.square
      }
    }
  }

  // This is an area substrate, so we'll prioritize the custom dimensions made at the substrate level
  if (item.dimensionOverride?.useCustom && item.dimensionOverride.custom) {
    value = item.dimensionOverride.custom
  }
  return { label, value }
}

const toNumber = (dimension: number | string) =>
  typeof dimension === 'number' ? dimension : Number.parseFloat(dimension)

// Same rounding as the area-grouped WO's getDimensionString, so both views print the same number
const decimalPrecision = (num: number) => Math.round(num * 100) / 100
